import React, { Dispatch, SetStateAction } from 'react';
import axios from '../../../modules/utils/axios.utils';
import { MACHINE_MANAGEMENT_RETRY_INTERVAL, MACHINE_MANAGEMENT_TOTAL_RETRIES } from '../../../modules/utils/env';
import { SnackBarContext } from '../../../modules/snackBar/contexts/SnackBarContext';
import getBrowserTimeZone from '../../../modules/shared/utils/returnTimeZone';

interface RuntimeMetricsAutoTreatContextInterface {
  fetchCachedRuntimeMetricsAutoTreat?: (machineSerial: string) => Promise<void>;
  fetchRuntimeMetricsAutoTreat?: (
    machineSerial: string,
    genericFilter: string,
    onSuccess?: () => void,
  ) => Promise<void>;
  runtimeMetricsAutoTreatRefreshedAt?: string;
  runtimeMetricsAutoTreat?: any[];
  runtimeMetricsAutoTreatLoading?: boolean;

  fetchAlarmHistory?: (machineSerial: string) => Promise<void>;
  alarmHistory?: any[];
  alarmHistoryLoading?: boolean;
  alarmHistoryRefreshedAt?: string;

  exportRuntimeMetrics?: (
    includeHeader: boolean,
    localTime: any,
    utcOffset: any,
  ) => Promise<void>;

  detailPage?: boolean;
  setDetailPage?: Dispatch<SetStateAction<boolean>>;

  setActiveMachine?: Dispatch<SetStateAction<Record<string, string>>>;
  activeMachine?: Record<string, string>;

  setActiveRuntimeMetricAutoTreat?: Dispatch<SetStateAction<Record<string, string>>>;
  activeRuntimeMetricAutoTreat?: Record<string, string>;

  handleGenericFilterChange?: (genericFilter: string) => Promise<void>;
  genericFilter?: string;
}

const RuntimeMetricsAutoTreatContext = React.createContext<RuntimeMetricsAutoTreatContextInterface>({});

const RuntimeMetricsAutoTreatContextConsumer = RuntimeMetricsAutoTreatContext.Consumer;
const RuntimeMetricsAutoTreatContextProvider: React.FC = ({ children }) => {
  const { showErrorSnackBar } = React.useContext(SnackBarContext);

  const [runtimeMetricsAutoTreat, setRuntimeMetricsAutoTreat] = React.useState([]);
  const [runtimeMetricsAutoTreatLoading, setRuntimeMetricsAutoTreatLoading] = React.useState(true);
  const [runtimeMetricsAutoTreatRefreshedAt, setRuntimeMetricsAutoTreatRefreshedAt] = React.useState<string>();
  const [activeRuntimeMetricAutoTreat, setActiveRuntimeMetricAutoTreat] = React.useState({});
  const [detailPage, setDetailPage] = React.useState<boolean>(false);
  const [activeMachine, setActiveMachine] = React.useState<Record<string, string>>({
    label: localStorage.getItem('machineManagementAutoTreatMachine') || 'Select A Machine',
    value: localStorage.getItem('machineManagementAutoTreatMachine') || 'Select A Machine',
  });
  const [genericFilter, setGenericFilter] = React.useState<string>('');
  const [alarmHistory, setAlarmHistory] = React.useState([]);
  const [alarmHistoryLoading, setAlarmHistoryLoading] = React.useState(false);
  const [alarmHistoryRefreshedAt, setAlarmHistoryRefreshedAt] = React.useState<string>();

  React.useEffect(() => {
    if (activeMachine?.value === 'Select A Machine') setRuntimeMetricsAutoTreatLoading(false);
  }, [activeMachine]);

  const fetchCachedRuntimeMetricsAutoTreat = async (machineSerial: string) => {
    setRuntimeMetricsAutoTreatLoading(true);
    setRuntimeMetricsAutoTreatRefreshedAt(null);

    axios
      .get<string, any>(`api/autotreat/onprem/runtime_metrics/${machineSerial}/?sql_endpoint=selectAutoTreatOrderRuntimeMetrics`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      })
      .then((response) => {
        setRuntimeMetricsAutoTreatLoading(false);
        setRuntimeMetricsAutoTreat(response.data.results);
        setRuntimeMetricsAutoTreatRefreshedAt(response.data.created_at);
      })
      .catch((error) => {
        setRuntimeMetricsAutoTreatLoading(false);
        setRuntimeMetricsAutoTreat([]);
        setRuntimeMetricsAutoTreatRefreshedAt(null);
      });
  };

  const fetchRuntimeMetricsAutoTreat = async (
    machineSerial: string,
    genericFilter: string,
    onSuccess?: () => void,
  ) => {
    setRuntimeMetricsAutoTreatLoading(true);
    setRuntimeMetricsAutoTreatRefreshedAt(null);
    let retryCount = 0;

    // Get date filter values from localStorage
    const startDate = localStorage.getItem('startDate') || '';
    const endDate = localStorage.getItem('endDate') || '';
    const timePeriod = localStorage.getItem('timePeriod') || '';
    const browser_tz = getBrowserTimeZone();

    // Build query params with date filters
    let queryParams = `serial-number=${machineSerial}&generic-filter=${genericFilter}`;
    if (startDate) queryParams += `&start=${encodeURIComponent(startDate)}`;
    if (endDate) queryParams += `&end=${encodeURIComponent(endDate)}`;
    if (timePeriod && !startDate && !endDate) queryParams += `&time-period=${encodeURIComponent(timePeriod)}`;
    if (browser_tz) queryParams += `&browser_tz=${encodeURIComponent(browser_tz)}`;

    axios
      .get<string, any>(
        `api/autotreat/onprem/runtime_metrics/?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
        },
      )

      .then((res) => {
        const fetchTimer = window.setInterval(() => {
          axios
            .get<string, any>(`api/autotreat/onprem/runtime_metrics/${res.data.result}/?sql_endpoint=selectAutoTreatOrderRuntimeMetrics`, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
            })
            .then((response) => {
              window.clearTimeout(fetchTimer);
              setRuntimeMetricsAutoTreatLoading(false);
              setRuntimeMetricsAutoTreat(response.data.results);
              setRuntimeMetricsAutoTreatRefreshedAt(response.data.created_at);
            })
            .catch((error) => {
              retryCount += 1;

              if (retryCount > MACHINE_MANAGEMENT_TOTAL_RETRIES) {
                window.clearInterval(fetchTimer);
                showErrorSnackBar('Unable to load Runtime Metrics');
                setRuntimeMetricsAutoTreatLoading(false);
                setRuntimeMetricsAutoTreat([]);
                setRuntimeMetricsAutoTreatRefreshedAt(null);
              }
            });
        }, MACHINE_MANAGEMENT_RETRY_INTERVAL);
      });
  };

  const handleGenericFilterChange = async (genericFilter: string) => {
    setGenericFilter(genericFilter);
  };

  const fetchAlarmHistory = async (machineSerial: string) => {
    setAlarmHistoryLoading(true);
    setAlarmHistoryRefreshedAt(null);
    let retryCount = 0;

    // Get date filter values from localStorage
    const startDate = localStorage.getItem('startDate') || '';
    const endDate = localStorage.getItem('endDate') || '';
    const timePeriod = localStorage.getItem('timePeriod') || '';
    const browser_tz = getBrowserTimeZone();

    // Build query params with date filters
    let queryParams = `serial-number=${machineSerial}`;
    if (startDate) queryParams += `&start=${encodeURIComponent(startDate)}`;
    if (endDate) queryParams += `&end=${encodeURIComponent(endDate)}`;
    if (timePeriod && !startDate && !endDate) queryParams += `&time-period=${encodeURIComponent(timePeriod)}`;
    if (browser_tz) queryParams += `&browser_tz=${encodeURIComponent(browser_tz)}`;

    axios
      .get<string, any>(
        `api/autotreat/onprem/runtime_metrics/alarm-history/?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
        },
      )
      .then((res) => {
        const fetchTimer = window.setInterval(() => {
          axios
            .get<string, any>(`api/autotreat/onprem/runtime_metrics/${res.data.result}/?sql_endpoint=selectAutoTreatAlarmRuntimeMetrics`, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
            })
            .then((response) => {
              window.clearTimeout(fetchTimer);
              setAlarmHistoryLoading(false);
              setAlarmHistory(response.data.results);
              setAlarmHistoryRefreshedAt(response.data.created_at);
            })
            .catch((error) => {
              retryCount += 1;

              if (retryCount > MACHINE_MANAGEMENT_TOTAL_RETRIES) {
                window.clearInterval(fetchTimer);
                showErrorSnackBar('Unable to load Alarm History');
                setAlarmHistoryLoading(false);
                setAlarmHistory([]);
                setAlarmHistoryRefreshedAt(null);
              }
            });
        }, MACHINE_MANAGEMENT_RETRY_INTERVAL);
      })
      .catch((error) => {
        setAlarmHistoryLoading(false);
        setAlarmHistory([]);
        showErrorSnackBar('Unable to load Alarm History');
      });
  };

  const exportRuntimeMetrics = async (
    includeHeader: boolean,
    localTime: any,
    utcOffset: any,
  ) => {
    const startDate = localStorage.getItem('startDate') || '';
    const endDate = localStorage.getItem('endDate') || '';
    const timePeriod = localStorage.getItem('timePeriod') || '';
    const browser_tz = getBrowserTimeZone();

    // Request blob response type for direct download
    const config: any = {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      responseType: 'blob',
    };

    return axios
      .post<any, any>(
        `api/autotreat/onprem/runtime_metrics/export/`,
        {
          serialNumber: activeMachine?.value,
          includeHeader,
          localTime,
          utcOffset,
          startDate,
          endDate,
          timePeriod,
          browser_tz,
        },
        config,
      )
      .then((response) => {
        // Trigger browser download
        if (response.data) {
          const blob = new Blob([response.data], { type: 'application/zip' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `runtime_metrics_export_${activeMachine?.value}_${new Date().toISOString().split('T')[0]}.zip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  return (
    <RuntimeMetricsAutoTreatContext.Provider
      value={{
        fetchCachedRuntimeMetricsAutoTreat,
        fetchRuntimeMetricsAutoTreat,
        handleGenericFilterChange,
        genericFilter,
        runtimeMetricsAutoTreat,
        runtimeMetricsAutoTreatRefreshedAt,
        runtimeMetricsAutoTreatLoading,

        fetchAlarmHistory,
        alarmHistory,
        alarmHistoryLoading,
        alarmHistoryRefreshedAt,

        exportRuntimeMetrics,

        detailPage,
        setDetailPage,

        setActiveRuntimeMetricAutoTreat,
        activeRuntimeMetricAutoTreat,

        setActiveMachine,
        activeMachine,
      }}
    >
      {children}
    </RuntimeMetricsAutoTreatContext.Provider>
  );
};

export {
  RuntimeMetricsAutoTreatContextProvider,
  RuntimeMetricsAutoTreatContextConsumer,
  RuntimeMetricsAutoTreatContext,
};
